import React, { useEffect, useState, useMemo } from 'react';
import {
    Table,
    Row,
    Col,
    Button,
    Modal,
    PaginationProps,
    Select,
} from 'antd';
import { DatePicker } from 'Components';
import { getRequest, deleteRequest, ResponseDataType } from 'api';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { ExclamationCircleFilled, PlusOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { ROLE, checkRole } from 'constant/role'; // ✅ Thêm dòng này

const { confirm } = Modal;
const { Option } = Select;

interface ClassYearDataType {
    classYearId: number;
    classId: number;
    classLevel: string;
    className: string;
    schoolId: number;
}

export const ClassYearIncome = () => {
    const [selectedYear, setSelectedYear] = useState<dayjs.Dayjs | null>(dayjs());
    const [startYear, setStartYear] = useState<number | null>(null);
    const [classYears, setClassYears] = useState<ClassYearDataType[]>([]);
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [pageSize, setPageSize] = useState<number>(20);
    const [totalItems, setTotalItems] = useState<number>(0);
    const [sort, setSort] = useState<string>('asc');
    const [showCreateModal, setShowCreateModal] = useState(false);

    // Thêm state cho Modal Tổng hợp khoản thu
    const [showSummaryModal, setShowSummaryModal] = useState(false);
    const [selectedClassName, setSelectedClassName] = useState('');
    const [summaryData, setSummaryData] = useState<any>(null);
    const [loadingSummary, setLoadingSummary] = useState(false);

    const totalSummaryAmount = useMemo(() => {
        if (!summaryData?.students || !summaryData?.incomeHeaders) return 0;
        return summaryData.students.reduce((acc: number, student: any) => {
            const studentSum = summaryData.incomeHeaders.reduce((sAcc: number, header: any) => {
                return sAcc + (Number(student.incomes?.[header.incomeSchoolId]) || 0);
            }, 0);
            return acc + studentSum;
        }, 0);
    }, [summaryData]);

    const fetchSummaryData = async (classYearId: number) => {
        setLoadingSummary(true);
        try {
            const res = await getRequest(`/cms/accountant/income-student-register/summary-by-class-year?classYearId=${classYearId}`);
            if (res?.code === 200) {
                setSummaryData(res.info);
            } else {
                toast.error(res?.msg || 'Không thể tải dữ liệu tổng hợp');
                setSummaryData(null);
            }
        } catch (error) {
            toast.error('Lỗi khi tải dữ liệu tổng hợp');
            setSummaryData(null);
        }
        setLoadingSummary(false);
    };

    const sortBy = 'className';
    const navigate = useNavigate();

    const schoolIdStr = localStorage.getItem('schoolId');
    const schoolId = schoolIdStr ? parseInt(schoolIdStr, 10) : null;

    const role = localStorage.getItem('role');
    const isAdmin = checkRole(ROLE.ADMIN);
    const isAccountant = checkRole(ROLE.ACCOUNTANT);
    const fetchClassYears = async (year: number) => {
        if (!schoolId || isNaN(schoolId)) {
            toast.error('Không tìm thấy schoolId – vui lòng đăng nhập lại');
            return false;
        }

        const queryParams = new URLSearchParams({
            start: year.toString(),
            schoolId: schoolId.toString(),
            page: currentPage.toString(),
            perPage: pageSize.toString(),
            sort,
            sortBy,
        });

        const url = `/cms/class/get-class-years-by-start-end-schoolId?${queryParams.toString()}`;

        try {
            const response: ResponseDataType<{
                list: ClassYearDataType[];
                total: number;
            }> = await getRequest(url);

            if (response?.code === 200) {
                const hasData = (response.info?.list?.length || 0) > 0;
                if (hasData) {
                    setStartYear(year);
                    setSelectedYear(dayjs().year(year));
                    setClassYears(response.info?.list || []);
                    setTotalItems(response.info?.total || 0);
                } else {
                    setClassYears([]);
                    setTotalItems(0);
                }
                return hasData;
            } else {
                toast.error(response?.msg || 'Lỗi khi tải danh sách lớp');
            }
        } catch (error) {
            toast.error('Không thể kết nối đến máy chủ');
        }

        return false;
    };

    useEffect(() => {
        const loadInitialData = async () => {
            const currentYear = dayjs().year();
            const hasData = await fetchClassYears(currentYear);
            if (!hasData) {
                await fetchClassYears(currentYear - 1);
            }
        };
        loadInitialData();
    }, []);

    useEffect(() => {
        if (startYear !== null && !isNaN(startYear)) {
            fetchClassYears(startYear);
        }
    }, [currentPage, pageSize, sort]);

    const handleSearch = async () => {
        if (!selectedYear) {
            toast.warn('Vui lòng chọn năm');
            return;
        }
        const year = selectedYear.year();
        setCurrentPage(1);
        await fetchClassYears(year);
    };

    const handlePageChange: PaginationProps['onChange'] = (page, size) => {
        setCurrentPage(page);
        setPageSize(size || 10);
    };

    const showDeleteConfirm = (classYearId: number) => {
        confirm({
            title: 'Bạn có chắc chắn muốn xóa lớp này?',
            icon: <ExclamationCircleFilled />,
            content: 'Hành động này không thể hoàn tác.',
            okText: 'Xác nhận',
            okType: 'danger',
            cancelText: 'Hủy',
            async onOk() {
                const url = `/cms/class/delete-class-year/${classYearId}`;
                const response: ResponseDataType<{}> = await deleteRequest(url);
                if (response?.code === 200) {
                    toast.success('Xóa lớp thành công');
                    if (startYear) fetchClassYears(startYear);
                } else {
                    toast.error(response?.msg || 'Xóa thất bại');
                }
            },
        });
    };

    const columns: ColumnsType<ClassYearDataType> = [
        {
            title: 'STT',
            key: 'stt',
            align: 'center',
            width: 80,
            render: (text, record, index) => (currentPage - 1) * pageSize + index + 1,
        },
        { title: 'Tên lớp', dataIndex: 'className', key: 'className', align: 'center' },
        { title: 'Khối lớp', dataIndex: 'classLevel', key: 'classLevel', align: 'center' },
        { title: 'Năm học', dataIndex: 'schoolYear', key: 'schoolYear', align: 'center' },
        { title: 'Mô tả', dataIndex: 'description', key: 'description', align: 'center' },

        {
            title: 'Hành động',
            key: 'action',
            dataIndex: 'classYearId',
            align: 'center',
            render: (classYearId: number, record: ClassYearDataType) => (
                <>
                    <span
                        style={{ color: '#1677ff', fontWeight: 600, cursor: 'pointer', marginRight: 10 }}
                        onClick={() => navigate(`/class-year-income?classYearId=${classYearId}`)}
                    >
                        Bảng ĐK mới (3 vùng)
                    </span>
                    <span
                        style={{ color: 'blue', cursor: 'pointer' }}
                        onClick={() => navigate(`/detail-class-year-income/${classYearId}`)}
                    >
                        In biên lai (P1)
                    </span>
                    <span
                        style={{ color: 'green', marginLeft: 10, cursor: 'pointer' }}
                        onClick={() => {
                            setSelectedClassName(record.className);
                            fetchSummaryData(classYearId);
                            setShowSummaryModal(true);
                        }}
                    >
                        Tổng hợp khoản thu
                    </span>
                    {!isAccountant && (
                        <span
                            style={{ color: 'red', marginLeft: 10, cursor: 'pointer' }}
                            onClick={() => showDeleteConfirm(classYearId)}
                        >
                            Xóa
                        </span>
                    )}
                </>
            ),
        },
    ];

    return (
        <div>
            <Row gutter={8} style={{ marginBottom: 16, justifyContent: 'space-between' }}>
                <Col>
                    <DatePicker
                        picker="year"
                        placeholder="Chọn năm bắt đầu"
                        value={selectedYear}
                        onChange={(date) => setSelectedYear(date)}
                        style={{ width: 200, marginRight: 8 }}
                    />
                    <Button type="primary" onClick={handleSearch} style={{ marginRight: 8 }}>
                        Tìm kiếm
                    </Button>
                    <Select value={sort} onChange={setSort} style={{ width: 140 }}>
                        <Option value="asc">Tên lớp A → Z</Option>
                        <Option value="desc">Tên lớp Z → A</Option>
                    </Select>
                </Col>

                {!isAccountant && (
                    <Col>
                        <Button
                            type="primary"
                            icon={<PlusOutlined />}
                            onClick={() => setShowCreateModal(true)}
                        >
                            Thêm năm học
                        </Button>
                    </Col>
                )}
            </Row>

            <Table
                columns={columns}
                dataSource={classYears}
                rowKey="classYearId"
                pagination={{
                    current: currentPage,
                    pageSize: pageSize,
                    total: totalItems,
                    showSizeChanger: true,
                    pageSizeOptions: ['10', '20', '50'],
                    onChange: handlePageChange,
                }}
            />

            <Modal
                title={
                    <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        paddingRight: 32,
                    }}>
                        <span style={{ fontSize: 16, fontWeight: 600 }}>
                            Danh sách khoản thu - {selectedClassName}
                        </span>
                        <span style={{ fontSize: 14, fontWeight: 'normal', color: '#262626' }}>
                            Tổng tiền: <strong style={{ fontWeight: 600 }}>{totalSummaryAmount.toLocaleString('vi-VN')} đ</strong>
                        </span>
                    </div>
                }
                open={showSummaryModal}
                onCancel={() => setShowSummaryModal(false)}
                footer={null}
                zIndex={9999}
                width={Math.min(
                    Math.max(600, 460 + (summaryData?.incomeHeaders?.length || 0) * 150),
                    window.innerWidth * 0.85
                )}
                style={{ maxWidth: '85vw' }}
            >
                <Table
                    bordered
                    loading={loadingSummary}
                    dataSource={summaryData?.students || []}
                    rowKey="studentId"
                    pagination={false}
                    scroll={{ x: 'max-content', y: 450 }}
                    summary={(pageData) => {
                        let totalSum: { [key: number]: number } = {};
                        let grandTotal = 0;
                        pageData.forEach((student: any) => {
                            summaryData?.incomeHeaders?.forEach((header: any) => {
                                const val = Number(student.incomes?.[header.incomeSchoolId]) || 0;
                                totalSum[header.incomeSchoolId] = (totalSum[header.incomeSchoolId] || 0) + val;
                                grandTotal += val;
                            });
                        });
                        return (
                            <Table.Summary.Row style={{ backgroundColor: '#fafafa', fontWeight: 'bold' }}>
                                <Table.Summary.Cell index={0} colSpan={4} align="center">
                                    Tổng cộng: {grandTotal.toLocaleString()} đ
                                </Table.Summary.Cell>
                                {summaryData?.incomeHeaders?.map((header: any, index: number) => (
                                    <Table.Summary.Cell index={index + 4} key={header.incomeSchoolId} align="right">
                                        {totalSum[header.incomeSchoolId]?.toLocaleString()} đ
                                    </Table.Summary.Cell>
                                ))}
                            </Table.Summary.Row>
                        );
                    }}
                    columns={[
                        { title: 'STT', key: 'stt', width: 60, align: 'center', render: (_, __, index) => index + 1 },
                        { title: 'CCCD', dataIndex: 'cmnd', key: 'cmnd', width: 120 },
                        { title: 'Họ tên', dataIndex: 'name', key: 'name', width: 180 },
                        { title: 'Lớp', dataIndex: 'className', key: 'className', width: 100 },
                        ...(summaryData?.incomeHeaders || []).map((header: any) => ({
                            title: header.name,
                            key: `income_${header.incomeSchoolId}`,
                            align: 'right' as const,
                            render: (_: any, record: any) => {
                                const amount = record.incomes[header.incomeSchoolId] || 0;
                                return `${amount.toLocaleString()} đ`;
                            }
                        }))
                    ]}
                />
            </Modal>

            {/* <Modal
                title="Tạo năm học mới"
                open={showCreateModal}
                onCancel={() => setShowCreateModal(false)}
                footer={null}
                destroyOnClose
                width={800}
            >
                <CreateClassYear
                    onSuccess={() => {
                        if (startYear) fetchClassYears(startYear);
                        setShowCreateModal(false);
                    }}
                />
            </Modal> */}
        </div>
    );
};
